import {
    EFFICIENCY_CLASS_TMEF,
    EFFICIENCY_CLASS,
    EFFICIENCY_CLASS_LEXUS,
} from "../../../../common-deprecated/constants";
import type {
    ApiColourType,
    ApiGradeBodyEngineTransmissionWheeldriveType,
    ApiGradeVisualForGradeConfigType,
    ApiPriceType,
} from "../../../../common-deprecated/types/ApiModel";
import { EcoTagType } from "../../../../common-deprecated/types/CommonTypes";
import { getGrLogo as getGrLogoCommon } from "../../../../common-deprecated/utils";
import type { GradeSelectorFiltersType } from "../redux/slices/GradeSelectorFiltersSlice";
import type {
    ColorType,
    EngineSpecType,
    EquipmentGroupType,
    GradeBodyTypeEngineTransmissionType,
    GradeBodyTypeEngineTransmissionWheeldriveType,
    GradeBodyTypeEngineType,
    GradeBodyTypeType,
} from "./types/Model";
import { getEcoTagType } from "../../shared-logic/utils/ecoUtils";
import { getUniqueGradeBodyTypeId } from "../../../../common-deprecated/features/apheleia/utils/aphUtils";
import type { SelectedGradeType } from "../redux/slices/GradeSelectorCompareSlice";
import type { CmcModelDataType } from "../../shared-logic/types/CmcModelDataType";

// Info
export const getName = (gradeBodyType: GradeBodyTypeType): string => {
    const wheeldrive = gradeBodyType.engines[0]?.transmissions[0]?.wheeldrives[0];

    return `${wheeldrive.webTitleSections?.Model || ""} ${wheeldrive.webTitleSections?.Grade || ""}`.trim();
};

export const diffWeeks = (dt2: Date, dt1: Date): number => {
    let diff = (dt2.getTime() - dt1.getTime()) / 1000;
    diff /= 60 * 60 * 24 * 7;
    return Math.abs(Math.round(diff));
};

export const getGRLogo = (gradeBodyType: GradeBodyTypeType): ReturnType<typeof getGrLogoCommon> => {
    const wheeldrive = gradeBodyType.engines[0]?.transmissions[0]?.wheeldrives[0];
    return getGrLogoCommon(wheeldrive.webTitleSections?.Model || "", wheeldrive.webTitleSections?.Grade || "");
};

// Specs
export const getSpec = (
    wheeldrive: GradeBodyTypeEngineTransmissionWheeldriveType,
    specCode: string,
): EngineSpecType | undefined => wheeldrive.specs.find(({ code }) => code === specCode);

export const getEcoSpec = (wheeldrive: GradeBodyTypeEngineTransmissionWheeldriveType): EngineSpecType | undefined =>
    wheeldrive.specs.find(
        ({ code }) =>
            code.toUpperCase() === EFFICIENCY_CLASS_TMEF ||
            code.toUpperCase() === EFFICIENCY_CLASS ||
            code.toUpperCase() === EFFICIENCY_CLASS_LEXUS,
    );

export type MotorizationItemType = {
    engine: GradeBodyTypeEngineType;
    transmission: GradeBodyTypeEngineTransmissionType;
    wheeldrive: GradeBodyTypeEngineTransmissionWheeldriveType;
};

export const getMotorizations = (
    gradeBodyType: GradeBodyTypeType,
    activeFilters?: GradeSelectorFiltersType["filters"],
): MotorizationItemType[] => {
    const motorizations: MotorizationItemType[] = [];
    gradeBodyType.engines.forEach((engine) => {
        engine.transmissions.forEach((transmission) => {
            transmission.wheeldrives.forEach((wheeldrive) => {
                if (activeFilters?.engine) {
                    const ecoTag: EcoTagType | undefined = Object.values(EcoTagType).find(
                        (value) => value === activeFilters.engine,
                    );
                    if (ecoTag !== getEcoTagType(engine)) return;
                }
                if (activeFilters?.wheelDrive && wheeldrive.code !== activeFilters.wheelDrive) return;
                if (activeFilters?.transmission && transmission.code !== activeFilters.transmission) return;

                motorizations.push({
                    engine,
                    transmission,
                    wheeldrive,
                });
            });
        });
    });

    return motorizations;
};

export const getFullEngineCode = (
    engine: GradeBodyTypeEngineType,
    transmission: GradeBodyTypeEngineTransmissionType,
    wheeldrive: GradeBodyTypeEngineTransmissionWheeldriveType,
): string => `${engine.code}${transmission.code}${wheeldrive.code}`;

export const getColoursOfGradeBodyType = (gradeBodyType: GradeBodyTypeType): ColorType[] =>
    gradeBodyType.engines.flatMap((engine) =>
        engine.transmissions.flatMap((transmission) =>
            transmission.wheeldrives.flatMap((wheeldrive) => wheeldrive.colors),
        ),
    );

/**
 * Searches for the corresponding colour code that matches the state's selected colour id.
 * If no selected color id is present, it returns the code of the first color in the collection.
 */
export const getColourCodeById = (gradeBodyType: GradeBodyTypeType): string => {
    const colours = getColoursOfGradeBodyType(gradeBodyType);

    const colourCode = colours.find(({ id }) => id === gradeBodyType.selectedColourId)?.code;

    if (gradeBodyType.selectedColourId && colourCode) return colourCode;

    return colours[0].code;
};

export const getUniqueMotorizationId = (
    engine: GradeBodyTypeEngineType,
    transmission: GradeBodyTypeEngineTransmissionType,
    wheeldrive: GradeBodyTypeEngineTransmissionWheeldriveType,
): string => `${engine.id}-${transmission.id}-${wheeldrive.id}`;

export const getMotorizationData = (
    gradeBodyType: GradeBodyTypeType,
): {
    engine: GradeBodyTypeEngineType;
    transmission: GradeBodyTypeEngineTransmissionType;
    wheeldrive: GradeBodyTypeEngineTransmissionWheeldriveType;
} => {
    const { selectedMotorization } = gradeBodyType;

    const engine =
        gradeBodyType.engines.find(({ id }) => id === selectedMotorization.engineId) || gradeBodyType.engines[0];
    const transmission =
        engine?.transmissions.find(({ id }) => id === selectedMotorization.transmissionId) || engine.transmissions[0];
    const wheeldrive =
        transmission?.wheeldrives.find(({ id }) => id === selectedMotorization.wheeldriveId) ||
        transmission.wheeldrives[0];

    return { engine, transmission, wheeldrive };
};

export const calculatePriceWithColour = (
    inclVat: boolean,
    gradePrice: number,
    colourPrice: ApiPriceType | undefined,
    oldPrice = false,
): number => {
    const priceKey = oldPrice ? (inclVat ? "list" : "net") : inclVat ? "listWithDiscount" : "netWithDiscount";

    return (colourPrice?.[priceKey] ?? 0) + gradePrice;
};

export const getColourPrice = (
    colourId: string | undefined,
    selectedWheelDrive: GradeBodyTypeEngineTransmissionWheeldriveType,
): ApiPriceType | undefined => {
    return selectedWheelDrive.colors?.find(({ id }) => colourId === id)?.price;
};

/**
 * Searches in all available colours for a match of visualForGradeConfig.
 * If no match is found, the first available colour for that wheeldrive is returned.
 */
export const getColourIdFromVisualForGrade = (
    wheeldrive: ApiGradeBodyEngineTransmissionWheeldriveType,
    colours: ApiColourType[],
    visualForGradeConfig?: ApiGradeVisualForGradeConfigType,
): string | undefined => {
    const matchingWheeldriveColourCode = wheeldrive.colours.find(
        (colour) => colour === visualForGradeConfig?.colour.code,
    );

    const matchingColour = colours.find(
        (colour) => colour.code === matchingWheeldriveColourCode && colour.id === visualForGradeConfig?.colour.id,
    )?.id;

    return matchingColour ?? colours.find(({ code }) => code === wheeldrive.colours[0])?.id;
};

export const getCategoryGroupIcon = (
    equipmentGroups: EquipmentGroupType[],
    categoryRoot: string,
): "car" | "interior_car" | undefined => {
    const item = equipmentGroups.find((group) => group.root === categoryRoot)?.default;
    const root = item?.root?.toLowerCase();
    const path = item?.path?.toLowerCase();

    if (root === "exterior" || path?.includes("/exterior")) return "car";
    else if (root === "interior" || path?.includes("/interior")) return "interior_car";

    return undefined;
};

export const getGradesToCompare = (
    selectedGradeBodyTypes: (SelectedGradeType | null)[],
    allGradeBodyTypes: GradeBodyTypeType[],
): CmcModelDataType[] => {
    return selectedGradeBodyTypes
        .filter((grade): grade is SelectedGradeType => grade !== null)
        .sort((a, b) => a.gradeBodyTypeIndex - b.gradeBodyTypeIndex)
        .reduce<CmcModelDataType[]>((result, { uniqueBodyTypeId, colourId }) => {
            const gradeToAdd = allGradeBodyTypes.find(
                (grade) =>
                    getUniqueGradeBodyTypeId(grade.grade.id, grade.bodyType.id, grade.submodel?.id) ===
                    uniqueBodyTypeId,
            );

            if (gradeToAdd) {
                result.push({
                    gradeId: gradeToAdd.grade.id,
                    colourId,
                    motorization: gradeToAdd.selectedMotorization,
                    submodelId: gradeToAdd.submodel?.id,
                });
            }

            return result;
        }, []);
};
